// skill-map.tsx — the six skills as an entity map, on the board.
//
// WHY (Erez, 17 Aug 2026): "I want you to create a diagram that helps us start
// to visualize each skill and what other skills it connects to… think about it
// as an entity diagram, that we can show the classes and each class shows what
// are the main functions each skill has and then how it connects to other
// skills. Let's start visualizing this before my mind blows up."
//
// So this is a CLASS diagram, not a flow: a box per skill, its main steps as
// the members inside it, and a wire wherever one skill calls another or touches
// a store. It is meant to be edited on every pass through their blueprint — a
// step lands here the moment we read the section that specifies it.
//
// ── WHAT IT IS SHOWING, AND THE ONE THING THAT IS NEW ───────────────────────
// Until today the five skills were FLAT: none of them called another. Reading
// §1's Visual Cost Intelligence Database produced the sixth, `Comparable Jobs`,
// and it is the first skill that exists because TWO others need it. Erez's
// reasoning, verbatim: "if the logic to go and fetch similar jobs from past
// things is a whole thing by itself, then we shouldn't reproduce this entire
// logic in both skills."
//
// That makes the diagram worth having at all. Five independent boxes need no
// picture; the moment one is shared, the shape of the system is a thing you can
// get wrong, and a picture is how you notice.
//
// ── HONESTY RULES FOR THIS FILE ─────────────────────────────────────────────
// 1. A step appears here ONLY when their document has specified it. We have
//    walked §1 and part of §3, so most of these come from §1's engine
//    definitions and the rest are marked open. An invented step is worse than a
//    thin box: it looks settled.
// 2. RED IS AN OPEN QUESTION, not a danger. `join key?` is red because nothing
//    combines six sources without one and we do not yet know if they have it.
//    Answering a red box is the work; deleting one is not.
// 3. Empty is honest. Vendor Pricing has three steps because §1 gave us three.
//
// Engine: board/command/c4.tsx, per skill://diagrams. Nothing here has a
// hand-written width — a leaf measures its text, a box measures its children.
// ⚠ Skills as SEPARATE TOP-LEVEL COLUMNS on purpose: c4 paints wires first and
// boxes over them, so a wire whose whole path sits inside a container is
// invisible. Between columns it always draws.

import { Canvas, box, leaf, person, type Diagram } from "../command/c4";

const SKILLS: Diagram = (() => {
  // ── Person-triggered. A human starts these. ───────────────────────────────
  const ballpark = box("sm-ballpark", "Ballpark Estimate", "col", [
    leaf("bp-comps", "find comparables", "→ Comparable Jobs", "Search"),
    leaf("bp-route", "price the routes", undefined, "Route"),
    leaf("bp-band", "apply the band", undefined, "Percent"),
    leaf("bp-three", "three prices", undefined, "Tags"),
    leaf("bp-refuse", "refuse if thin", undefined, "ShieldAlert", "gate"),
  ]);

  const detailed = box("sm-detailed", "Detailed Estimate", "col", [
    leaf("dp-comps", "refresh comparables", "→ Comparable Jobs", "RefreshCw"),
    leaf("dp-bom", "build the BoM", undefined, "ListTree"),
    leaf("dp-quotes", "validate quotes", undefined, "CheckCheck"),
    leaf("dp-anom", "flag anomalies", undefined, "TriangleAlert"),
    leaf("dp-scen", "budget scenarios", undefined, "SlidersHorizontal"),
  ]);

  const learning = box("sm-learning", "Estimate Learning", "col", [
    leaf("pl-out", "capture outcome", undefined, "Flag"),
    leaf("pl-vs", "quoted vs final", undefined, "GitCompare"),
    leaf("pl-prop", "propose a change", undefined, "PenLine"),
  ]);

  // ── The sixth, and the only one no person triggers. ───────────────────────
  const comparable = box("sm-cj", "Comparable Jobs", "col", [
    leaf("cj-brief", "read the brief", undefined, "FileText"),
    leaf("cj-draw", "match drawings", undefined, "Image"),
    leaf("cj-attr", "match attributes", undefined, "Table2"),
    leaf("cj-rank", "weight and rank", undefined, "ArrowDownWideNarrow"),
    leaf("cj-enough", "enough to stand on?", undefined, "ShieldQuestion", "gate"),
  ], { tech: "[shared · called by 2]" });

  // ── The seventh, and the only one that leaves the building. It does NOT
  //    return: the pack goes out to vendors and days pass. Modelled as a call
  //    inside Detailed, Detailed becomes a week-long process — so it is its own
  //    step, and the quotes land ON THE JOB. ⚠ Its TRIGGER is Q16, unanswered:
  //    their document runs both a monthly vendor sweep and a per-job pack.
  const costing = box("sm-ct", "Costing Template", "col", [
    leaf("ct-gaps", "find the gaps", undefined, "ScanSearch", "gate"),
    leaf("ct-vendors", "choose vendors", undefined, "Users"),
    leaf("ct-pack", "build the pack", undefined, "ClipboardList"),
    leaf("ct-anchor", "anchor the number", undefined, "Target"),
    leaf("ct-send", "a PERSON sends it", undefined, "Hand"),
    leaf("ct-parse", "parse what returns", undefined, "Import"),
  ], { tech: "[leaves the building]" });

  // ── Background. These fill stores; nobody waits on them. ──────────────────
  const vendor = box("sm-vendor", "Vendor Pricing", "col", [
    leaf("vp-in", "ingest monthly", undefined, "Download"),
    leaf("vp-norm", "normalise", undefined, "Shuffle"),
    leaf("vp-curve", "cost curves", undefined, "TrendingUp"),
  ]);

  const market = box("sm-market", "Market Price", "col", [
    leaf("mp-bench", "benchmark price", undefined, "Scale"),
    leaf("mp-dev", "deviation flag", undefined, "AlertTriangle"),
  ]);

  // ── Stores. Not skills — this is the thing §1 calls the Visual Cost
  //    Intelligence Database, and it is DATA, which is exactly why the
  //    similarity logic is a skill and the repository is not.
  const corpus = box("sm-corpus", "The corpus", "col", [
    leaf("st-draw", "drawings index", undefined, "Images"),
    leaf("st-jobs", "job records", undefined, "Database"),
    leaf("st-rates", "reference rates", undefined, "Ruler"),
    leaf("st-key", "join key?", undefined, "KeyRound", "gate"),
  ]);

  const jobrec = box("sm-job", "The job", "col", [
    leaf("jb-comps", "comparables", undefined, "Layers"),
    leaf("jb-prop", "the proposal", undefined, "FileCheck"),
    leaf("jb-outcome", "outcome?", undefined, "CircleHelp", "gate"),
  ]);

  return {
    key: "sm",
    title: "Outform — the seven skills, and what they share",
    subtitle: "Entity map · v0.2 · from §1 and §1.2 · grows as we read their blueprint",
    padBottom: 96,
    cols: [
      { root: box("sm-actors", undefined, "col", [person("sm-user", "Estimator")], { pad: 0 }) },
      { root: box("sm-front", undefined, "col", [ballpark, detailed, learning], { pad: 0, gap: 26 }), align: "top" },
      { root: box("sm-mid", undefined, "col", [comparable, costing], { pad: 0, gap: 26 }) },
      { root: box("sm-bg", undefined, "col", [vendor, market], { pad: 0, gap: 26 }) },
      { root: box("sm-stores", undefined, "col", [corpus, jobrec], { pad: 0, gap: 26 }) },
    ],
    wires: [
      { a: "sm-user", b: "sm-ballpark", as: "r", bs: "l", label: "brief", num: 1 },
      { a: "sm-user", b: "sm-detailed", as: "r", bs: "l", label: "richer brief", num: 2 },
      { a: "sm-ballpark", b: "sm-cj", as: "r", bs: "l", label: "calls", num: 3 },
      { a: "sm-detailed", b: "sm-cj", as: "r", bs: "l", label: "calls", num: 4 },
      { a: "sm-cj", b: "sm-corpus", as: "r", bs: "l", label: "query", num: 5, dashed: true },
      { a: "sm-cj", b: "sm-job", as: "r", bs: "l", label: "writes comps", num: 6, dashed: true },
      // Costing Template: triggered off a gap in the estimate, and its answers
      // land on the job rather than returning to a caller.
      { a: "sm-detailed", b: "sm-ct", as: "r", bs: "l", label: "gaps", num: 7 },
      { a: "sm-ct", b: "sm-job", as: "r", bs: "l", label: "quotes", num: 8, dashed: true },
      { a: "sm-ct", b: "sm-corpus", as: "r", bs: "l", dashed: true },
      { a: "sm-vendor", b: "sm-corpus", as: "r", bs: "l", dashed: true },
      { a: "sm-market", b: "sm-corpus", as: "r", bs: "l", dashed: true },
      // Routed underneath: Estimate Learning sits three columns from the job
      // record, and a straight run would pass behind two boxes and vanish.
      { a: "sm-learning", b: "sm-job", as: "b", bs: "b", label: "outcome", dashed: true },
    ],
    legend: [
      { style: "solid", label: "solid = one skill calls another" },
      { style: "dash", label: "dashed = reads or writes a store" },
      { style: "red", label: "red = open — we have not read the answer yet" },
      { style: "dash", label: "Costing Template leaves the building — days, not milliseconds" },
    ],
  };
})();

export function OutformSkillMap() {
  return <Canvas d={SKILLS} mode="contrast" />;
}
